import React from 'react'
import * as yup from 'yup'
import { useRouter } from 'next/router'
import FormHandler, { FormHandlerConfItemType } from '../FormHandler/FormHandler'
import useTrans from '@hooks/useTrans'
import { WContainer } from '@components/atoms'
import styles from './RivenditoreForm.module.sass'
import {
  useCategorieProgettiQuery,
  useCollezioniQuery,
  useCreateContattoRivenditoreMutation,
  useProfessioniQuery,
} from '@gql/graphql'
import { nazioni } from '@utils/nazioni'
import { province } from '@utils/province'
import { RivenditoreType } from '@utils/types'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

interface Props {
  className?: string
  rivenditore: RivenditoreType
}

const RivenditoreForm = (props: Props) => {
  const { className = '', rivenditore } = props

  const t = useTrans()
  const { executeRecaptcha } = useGoogleReCaptcha()
  console.log(executeRecaptcha)
  const router = useRouter()

  const collezioniValues = useCollezioniQuery()?.data?.collezioni?.map((x) => ({
    label: x.title,
    value: x.slug,
  }))
  if (collezioniValues) collezioniValues.unshift({ label: '---------', value: '' })

  const categorieValues = useCategorieProgettiQuery()?.data?.categorieProgetti.map((x) => ({
    label: x.title,
    value: x.id.toString(),
  }))
  if (categorieValues) categorieValues.unshift({ label: '---------', value: '' })

  const professioniValues = useProfessioniQuery()?.data?.professioni.map((x) => ({
    label: x.title,
    value: x.codice,
  }))

  const [createContattoRivenditoreMutation] = useCreateContattoRivenditoreMutation()
  const conf: FormHandlerConfItemType[] = [
    {
      label: t('Sei un cliente privato o un professionista?'),
      type: 'radio',
      name: 'tipo',
      required: true,
      layout: 'full',
      initialValue: 'privato',
      options: [
        { label: 'Privato', value: 'privato' },
        { label: 'Professionista', value: 'professionista' },
      ],
    },
    {
      label: t('Categoria progetto'),
      placeholder: t('Scegli il progetto'),
      type: 'select',
      name: 'categoria',
      options: categorieValues,
      initialValue: '',
      isVisible: (values) => values.tipo === 'professionista',
    },
    {
      label: t('Scegli la collezione'),
      placeholder: t('Collezione'),
      type: 'select',
      name: 'collezione',
      options: collezioniValues,
      initialValue: '',
      layout: (values) => (values.tipo === 'professionista' ? undefined : 'full'),
    },
    {
      label: t('Nome'),
      placeholder: t('Inserisci il tuo nome'),
      type: 'text',
      name: 'nome',
      required: true,
    },
    {
      label: t('Cognome'),
      placeholder: t('Inserisci il tuo cognome'),
      type: 'text',
      name: 'cognome',
      required: true,
    },
    {
      label: t('Professione'),
      placeholder: t('Seleziona la professione'),
      type: 'select',
      options: professioniValues,
      name: 'professione',
      required: true,
      isVisible: (values) => values.tipo === 'professionista',
    },
    {
      label: t('Ragione sociale'),
      placeholder: t('Inserisci ragione sociale'),
      type: 'text',
      name: 'ragioneSociale',
      required: true,
      isVisible: (values) => values.tipo === 'professionista',
    },
    {
      label: t('Email'),
      placeholder: t('Inserisci la tua email'),
      type: 'email',
      name: 'email',
      required: true,
      validation: {
        email: true,
      },
    },
    {
      label: t('Telefono'),
      placeholder: t('Inserisci il tuo numero di telefono'),
      type: 'text',
      name: 'telefono',
      required: true,
    },
    {
      label: t('Indirizzo'),
      placeholder: t('Inserisci il tuo indirizzo'),
      type: 'text',
      name: 'indirizzo',
      required: true,
    },
    {
      label: t('Città'),
      placeholder: t('Inserisci la tua città'),
      type: 'text',
      name: 'citta',
      required: true,
    },
    {
      label: t('CAP'),
      placeholder: t('Inserisci il CAP'),
      type: 'text',
      name: 'cap',
      required: true,
    },
    {
      label: t('Nazione'),
      placeholder: t('Seleziona la nazione'),
      type: 'select',
      name: 'nazione',
      isSearchable: true,
      options: nazioni.map((x) => ({ label: x.nome, value: x.codice })),
      required: true,
    },
    {
      label: t('Provincia'),
      placeholder: t('Seleziona la provincia'),
      type: 'select',
      name: 'provincia',
      options: province.map((x) => ({ label: x.nome, value: x.sigla })),
      isSearchable: true,
      required: true,
      layout: 'full',
      isVisible: (values) => values.nazione === 'IT',
    },
    {
      label: t('Messaggio'),
      placeholder: t('Inserisci qui la richiesta'),
      type: 'textarea',
      name: 'messaggio',
      required: true,
      layout: 'full',
    },
    {
      label: t(
        '<p>Preso atto del contenuto dell’ informativa rilasciata ai sensi dell’articolo 13 del GDPR che dichiaro di aver letto e compreso:</p>'
      ),
      type: 'group',
      layout: 'full',
      children: [
        {
          label: t(
            'Acconsento al trattamento dei miei dati personali da parte del titolare per la finalità di marketing, anche attraverso modalità automatizzate, indicata al punto 2.5 dell’<a href="{0}">informativa sulla privacy</a>',['/privacy-policy']
          ),
          type: 'checkbox',
          name: 'marketing',
          layout: 'full',
        },
        {
          label: t(
            'Acconsento al trattamento dei miei dati personali da parte del titolare per la finalità di profilazione indicata nel punto 2.6 della <a href="{0}">informativa sulla privacy</a>', ['/privacy-policy']
          ),
          type: 'checkbox',
          name: 'privacy',
          required: true,
          layout: 'full',
        },
      ],
    },
  ]

  const validationSchema = yup.object().shape({
    tipo: yup.string().required(t('Questo campo è obbligatorio')),
    nome: yup.string().required(t('Questo campo è obbligatorio')),
    cognome: yup.string().required(t('Questo campo è obbligatorio')),
    professione: yup.string().when('tipo', {
      is: 'professionista',
      then: yup.string().required(t('Questo campo è obbligatorio')),
    }),
    ragioneSociale: yup.string().when('tipo', {
      is: 'professionista',
      then: yup.string().required(t('Questo campo è obbligatorio')),
    }),
    email: yup
      .string()
      .required(t('Questo campo è obbligatorio'))
      .email(t("Il formato dell'email dev'essere valido")),
    telefono: yup.string().required(t('Questo campo è obbligatorio')),
    indirizzo: yup.string().required(t('Questo campo è obbligatorio')),
    citta: yup.string().required(t('Questo campo è obbligatorio')),
    cap: yup.string().required(t('Questo campo è obbligatorio')),
    nazione: yup.string().required(t('Questo campo è obbligatorio')),
    provincia: yup
      .string()
      .when('nazione', { is: 'IT', then: yup.string().required(t('Questo campo è obbligatorio')) }),
    messaggio: yup.string().required(t('Questo campo è obbligatorio')),
    privacy: yup
      .bool()
      .oneOf([true], t('Questo campo è obbligatorio'))
      .required(t('Questo campo è obbligatorio')),
  })

  const onSubmit = (values) => {
    const sendMutation = (token) => {
      createContattoRivenditoreMutation({
        variables: {
          input: {
            cliente: rivenditore.pk,
            tipo: values.tipo,
            ...(values.categoria ? { categoria: values.categoria } : {}),
            ...(values.collezione ? { collezione: values.collezione } : {}),
            nome: values.nome,
            cognome: values.cognome,
            profession: values.professione,
            ragiosoc: values.ragione_sociale,
            email: values.email,
            telefono: values.telefono,
            address: values.indirizzo,
            city: values.citta,
            zipCode: values.cap,
            countrycontact: values.nazione,
            provcontact: values.provincia,
            notes: values.messaggio,
            privacyOptSecond: values.marketing,
            privacyOpt: values.privacy,
            captcha: token,
          },
        },
      })
        .then((res) => {
          //setError(null)
          console.log(res)
          router.push(router.asPath + '/thank-you')
        })
        .catch((err) => {
          console.error(err)
          return
          //setFormError(actions)
        })
    }

    if (!executeRecaptcha && process.env.NODE_ENV !== 'development') {
      console.log('!executeRecaptcha')
      return
    }

    if (process.env.NODE_ENV !== 'development') {
      console.log('executeRecaptcha')
      executeRecaptcha('richiesta_contatto_submit')
        .then((token) => {
          console.log('executeRecaptcha', token)
          sendMutation(token)
        })
        .catch((err) => {
          console.error('Recaptcha error:', err)
          //setFormError(actions)
        })
    } else {
      sendMutation('test-token')
    }
  }

  return (
    <div className={`${styles.root} ${className}`}>
      <WContainer className={styles.container}>
        <FormHandler
          prefix="rivenditore"
          conf={conf}
          submitLabel={t('Contatta')}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        />
      </WContainer>
    </div>
  )
}

export default RivenditoreForm
